/**
 * Start a conversation with a customer by email (tracker item 56). Used on
 * the Inbox page and in the Conversations section of a customer or a job
 * (customer, and job, preset). The server refuses opted-out customers,
 * customers with no email and bounced addresses; those come back as clear
 * messages and nothing is sent.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createAPIClient, IConversationsApi, IJobsApi } from '../../api';
import * as types from '../../api/types';
import { CustomerPicker } from '../../components/CustomerPicker';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import { conversationErrorMessage, sendKey } from './inboxUi';

interface Props {
  /** Preset customer (customer / job pages): the picker is not shown */
  customer?: { id: string; name: string } | null;
  /** Preset job (job page) */
  jobId?: string | null;
  onCreated: (result: types.ConversationSendResult) => void;
  onCancel?: () => void;
}

export function NewConversationForm({ customer, jobId, onCreated, onCancel }: Props) {
  const api = useMemo<IConversationsApi & IJobsApi>(() => createAPIClient(), []);
  const [customerId, setCustomerId] = useState(customer?.id ?? '');
  const [linkJob, setLinkJob] = useState(jobId ?? '');
  const [jobs, setJobs] = useState<types.Job[]>([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // One key per form: a double click sends once
  const key = useRef(sendKey('new'));

  // The customer's jobs, to link one (not when the job is preset)
  useEffect(() => {
    if (jobId || !customerId) {
      setJobs([]);
      return undefined;
    }
    let active = true;
    void (async () => {
      try {
        const result = await api.listJobs(1, 100, { customerId });
        if (active) setJobs(result.data);
      } catch {
        if (active) setJobs([]);
      }
    })();
    return () => { active = false; };
  }, [api, customerId, jobId]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!customerId) return setError('Choose a customer.');
    if (!subject.trim()) return setError('Enter a subject.');
    if (!body.trim()) return setError('Write a message.');
    setSaving(true);
    try {
      const result = await api.createConversation({
        customerId,
        subject: subject.trim(),
        body,
        jobId: linkJob || null,
        idempotencyKey: key.current,
      });
      onCreated(result);
    } catch (cause) {
      setError(conversationErrorMessage(cause, 'Could not start the conversation'));
      // A refused send wrote nothing: a new attempt gets a new key
      key.current = sendKey('new');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={(e) => void submit(e)} className="space-y-3" aria-label="New conversation">
      {customer ? (
        <p className="text-sm text-gray-700">To <strong>{customer.name}</strong> by email</p>
      ) : (
        <CustomerPicker id="new-conversation-customer" label="Customer" value={customerId} onChange={(id) => { setCustomerId(id); setLinkJob(''); }} />
      )}
      {!jobId && jobs.length > 0 && (
        <div>
          <label htmlFor="new-conversation-job" className={labelClass}>About a job (optional)</label>
          <select id="new-conversation-job" className={inputClass} value={linkJob} onChange={(e) => setLinkJob(e.target.value)}>
            <option value="">No job</option>
            {jobs.map((j) => (
              <option key={j.id} value={j.id}>{[j.scheduledDate ?? 'Unscheduled', j.property?.address, j.status].filter(Boolean).join(' · ')}</option>
            ))}
          </select>
        </div>
      )}
      <div>
        <label htmlFor="new-conversation-subject" className={labelClass}>Subject</label>
        <input id="new-conversation-subject" className={inputClass} maxLength={200} value={subject} onChange={(e) => setSubject(e.target.value)} />
      </div>
      <div>
        <label htmlFor="new-conversation-body" className={labelClass}>Message</label>
        <textarea id="new-conversation-body" className={inputClass} rows={5} maxLength={10000} value={body} onChange={(e) => setBody(e.target.value)} />
      </div>
      {error && <p role="alert" className="rounded bg-red-50 p-3 text-sm text-red-800">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={primaryButtonClass} disabled={saving}>{saving ? 'Sending…' : 'Send email'}</button>
        {onCancel && <button type="button" className={secondaryButtonClass} onClick={onCancel} disabled={saving}>Cancel</button>}
      </div>
    </form>
  );
}
